import { useCallback, useSyncExternalStore } from 'react';
import {
  FetchOrderHistory,
  isFetchEligible,
  NO_FETCH_HISTORY,
  readFetchHistory,
  writeFetchHistory,
} from './fetchHistory';

// One server render is one visit, so the snapshot is held against its id: it
// stays stable across the renders of that visit, and a client navigation --
// which re-runs the page without reloading this module -- starts a new one.
let current: {
  key: string;
  history: FetchOrderHistory;
  recorded: boolean;
} | null = null;

function visitSnapshot(orderKey: string, visitId: string): FetchOrderHistory {
  const key = `${visitId}:${orderKey}`;
  if (current?.key !== key) {
    current = { key, history: readFetchHistory(orderKey), recorded: false };
  }
  return current.history;
}

function recordVisit(orderKey: string, visitId: string): void {
  // On a fresh page load the first render is hydration, which reads the server
  // snapshot, so the visit has to be taken here rather than found waiting.
  visitSnapshot(orderKey, visitId);
  if (!current || current.recorded) return;
  current.recorded = true;
  // Spent visits are not shown the offer, so counting them would record page
  // views rather than times Fetch was on the page.
  if (!isFetchEligible(current.history)) return;
  writeFetchHistory(orderKey, {
    ...current.history,
    visits: current.history.visits + 1,
  });
}

export function useFetchVisit(
  orderKey: string | undefined,
  visitId: string | undefined,
): FetchOrderHistory | null {
  // React subscribes once the render commits, so a render it discards -- an
  // interrupted transition, say -- spends nothing.
  const subscribe = useCallback(() => {
    if (orderKey && visitId) recordVisit(orderKey, visitId);
    return () => {};
  }, [orderKey, visitId]);

  return useSyncExternalStore(
    subscribe,
    () =>
      orderKey && visitId ? visitSnapshot(orderKey, visitId) : NO_FETCH_HISTORY,
    () => null,
  );
}
